import { useMemo } from 'react';
import { AirlineCredits } from '@shared-ui/retail-flights-airline-credits';

import { useStores } from '.';
import { FlightsJourneyCriteriaInput, FlightsTravelerDetailsInput } from '__generated__/api';

type AirlineCreditsInputsType = React.ComponentProps<typeof AirlineCredits>['inputs'];
type TravelerDetailsInputType = AirlineCreditsInputsType['travelerDetails'];
type JourneyCriteriasInputType = AirlineCreditsInputsType['journeyCriteria'];
type TravelerDetailInputType = TravelerDetailsInputType[number];
type JourneyCriteriaInputType = JourneyCriteriasInputType[number];
type FlightSearchContextInputsType = AirlineCreditsInputsType['flightsSearchContext'];

export const useAirlineCreditsInputs = () => {
  const { searchCriteriaStore } = useStores();
  const { searchFormCriteria } = searchCriteriaStore;
  const { travelerDetails, searchPreferences, journeyCriteria, flightsSearchContext } = searchFormCriteria;
  const { cabinClass, advancedFilters, airline } = searchPreferences;

  const {
    journeysContinuationId,
    originalBookingId,
    previousFlightSelections,
    previousOriginalBookingId,
    searchCriteriaMismatch,
    tripType
  } = flightsSearchContext;

  const inputs: AirlineCreditsInputsType = useMemo(() => {
    const travelerDetailsMapped: TravelerDetailsInputType = (travelerDetails as FlightsTravelerDetailsInput[]).map(
      (travelerDetail): TravelerDetailInputType => ({
        count: travelerDetail.count,
        travelerType: (travelerDetail.travelerType as unknown) as TravelerDetailInputType['travelerType'],
        ages: travelerDetail.ages ?? null
      })
    );

    const journeyCriteriaMapped: JourneyCriteriasInputType = (journeyCriteria as FlightsJourneyCriteriaInput[]).map(
      (journeyCriteria): JourneyCriteriaInputType => ({
        departureDate: {
          day: journeyCriteria.departureDate.day,
          month: journeyCriteria.departureDate.month,
          year: journeyCriteria.departureDate.year
        },
        destination: journeyCriteria.destination,
        origin: journeyCriteria.origin
      })
    );

    return {
      originalBookingId: originalBookingId,
      searchPreferences: {
        cabinClass,
        advancedFilters,
        airline
      },
      flightsSearchContext: {
        journeysContinuationId: journeysContinuationId ?? null,
        originalBookingId: originalBookingId ?? null,
        previousOriginalBookingId: previousOriginalBookingId ?? null,
        previousFlightSelections: previousFlightSelections,
        tripType: (tripType as unknown) as FlightSearchContextInputsType['tripType'],
        searchCriteriaMismatch: (searchCriteriaMismatch as unknown) as FlightSearchContextInputsType['searchCriteriaMismatch']
      },
      journeyCriteria: journeyCriteriaMapped,
      travelerDetails: travelerDetailsMapped
    };
  }, [
    travelerDetails,
    journeyCriteria,
    originalBookingId,
    cabinClass,
    advancedFilters,
    airline,
    journeysContinuationId,
    previousOriginalBookingId,
    previousFlightSelections,
    tripType,
    searchCriteriaMismatch
  ]);

  return inputs;
};
